import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getUsage, me, patchMe } from '../api/portal';
import { ApiError } from '../api/client';
import { useT } from '../lib/i18n';
import { entitlementsOf, hasHall, hasOffice, hasWhiteLabel } from '../lib/portalRole';

const ZONES = [
  'Europe/Belgrade',
  'Europe/Zagreb',
  'Europe/Sarajevo',
  'Europe/Podgorica',
  'Europe/Skopje',
  'Europe/Ljubljana',
  'Europe/Vienna',
  'Europe/Berlin',
  'UTC',
];

export function UsagePage() {
  const { t } = useT();
  const qc = useQueryClient();
  const usage = useQuery({ queryKey: ['portal-usage'], queryFn: getUsage, refetchInterval: 15_000 });
  const profile = useQuery({ queryKey: ['portal-me'], queryFn: me });
  const u = usage.data;
  const p = profile.data;
  const max = u?.days.reduce((m, d) => Math.max(m, d.requests), 1) ?? 1;
  const hour = u?.first_hour;
  const [tz, setTz] = useState('Europe/Belgrade');
  const [qhOn, setQhOn] = useState(false);
  const [qhStart, setQhStart] = useState('22:00');
  const [qhEnd, setQhEnd] = useState('08:00');
  const [mpOn, setMpOn] = useState(false);
  const [mpHour, setMpHour] = useState(8);
  const [sponsorLogo, setSponsorLogo] = useState('');
  const [sponsorLabel, setSponsorLabel] = useState('');
  const [poweredBy, setPoweredBy] = useState(true);
  const [slug, setSlug] = useState('');
  const [autoPack, setAutoPack] = useState(true);
  const [autoDigest, setAutoDigest] = useState(true);
  const [sponsorEmail, setSponsorEmail] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [logoErr, setLogoErr] = useState<string | null>(null);

  useEffect(() => {
    if (!p) return;
    setTz(p.timezone || 'Europe/Belgrade');
    setQhOn(!!p.quiet_hours_enabled);
    setQhStart((p.quiet_hours_start || '22:00').slice(0, 5));
    setQhEnd((p.quiet_hours_end || '08:00').slice(0, 5));
    setMpOn(!!p.morning_pack_enabled);
    setMpHour(p.morning_pack_hour ?? 8);
    setSponsorLogo(p.sponsor_logo_url || '');
    setSponsorLabel(p.sponsor_label || '');
    setPoweredBy(p.widget_powered_by !== false);
    setSlug(p.public_slug || '');
    setAutoPack(p.auto_round_pack !== false);
    setAutoDigest(p.auto_club_digest !== false);
    setSponsorEmail(p.sponsor_report_email || '');
  }, [p]);

  const save = useMutation({
    mutationFn: () => patchMe({
      timezone: tz,
      quiet_hours_enabled: qhOn,
      quiet_hours_start: qhStart,
      quiet_hours_end: qhEnd,
      morning_pack_enabled: mpOn,
      morning_pack_hour: mpHour,
      sponsor_logo_url: sponsorLogo,
      sponsor_label: sponsorLabel,
      public_slug: slug,
      auto_club_digest: autoDigest,
      ...(hasWhiteLabel(p) ? { widget_powered_by: poweredBy } : {}),
      ...(hasOffice(p) ? { auto_round_pack: autoPack } : {}),
      ...(hasHall(p) ? { sponsor_report_email: sponsorEmail } : {}),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal-me'] });
      setMsg(t('usage.saved'));
    },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : String(e)),
  });

  const zones = ZONES.includes(tz) ? ZONES : [tz, ...ZONES];
  const comps = (p?.allowed_competitions ?? []).map((c) => c.code || c.name);
  const sports = p?.allowed_sports ?? [];

  return (
    <div className="space-y-4">
      <div className="page-head">
        <div className="page-kicker">{t('usage.kicker')}</div>
        <h1 className="page-title">{t('usage.title')}</h1>
        <p className="page-sub">{t('usage.sub')}</p>
      </div>

      {!u?.onboarding?.completed && (
        <Link to="/setup" className="card block px-4 py-3 text-sm text-slate-300 hover:bg-white/[0.03]">
          {t('usage.setupOpen')}
        </Link>
      )}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label={t('usage.requests')} value={String(u?.requests_today ?? '—')} warn={u?.near_limit} />
        <Stat label={t('usage.mounts')} value={String(u?.impressions?.mounts ?? 0)} />
        <Stat label={t('usage.views')} value={String(u?.impressions?.views ?? 0)} />
        <Stat label={t('usage.keys')} value={`${u?.keys_used_today ?? 0} / ${u?.keys_active ?? 0}`} />
      </div>

      {hour && (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Stat label={t('usage.hourApi')} value={String(hour.requests)} />
          <Stat label={t('usage.hourOk')} value={String(hour.webhook_ok)} />
          <Stat label={t('usage.hourFail')} value={String(hour.webhook_failed)} warn={hour.webhook_failed > 0} />
          <Stat label={t('usage.lastDelivery')} value={hour.last_delivery_at ? hour.last_delivery_at.slice(11, 19) : '—'} />
        </div>
      )}

      <div className="grid gap-3 xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <div className="card p-4">
          <div className="mb-3 text-[11px] font-medium uppercase tracking-[0.08em] text-subtle">{t('usage.last7')}</div>
          {(u?.days ?? []).length > 0 ? (
            <div className="flex h-40 items-end gap-2">
              {u!.days.map((d) => (
                <div key={d.day} className="flex min-w-0 flex-1 flex-col items-center gap-1">
                  <div
                    className="w-full rounded-t bg-accent/80"
                    style={{ height: `${Math.max(8, (d.requests / max) * 100)}%` }}
                    title={`${d.day}: ${d.requests}`}
                  />
                  <div className="text-[10px] text-slate-500">{d.day.slice(5)}</div>
                  <div className="text-[10px] tabular-nums text-slate-400">{d.requests}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex h-40 items-center justify-center text-sm text-slate-500">{t('usage.noTraffic')}</div>
          )}
        </div>

        {u?.impressions && (u.impressions.by_widget.length > 0 || u.impressions.by_club.length > 0) ? (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
            <Breakdown title={t('usage.byWidget')} rows={(u.impressions.by_widget ?? []).map((row) => [`${row.widget} · ${row.event}`, row.count])} empty={t('usage.noEmbed')} />
            <Breakdown title={t('usage.byClub')} rows={(u.impressions.by_club ?? []).map((row) => [row.company_name, row.count])} empty={t('usage.noEmbed')} />
          </div>
        ) : null}
      </div>

      {p && (
        <div className="card p-4">
          <div className="mb-3 text-[11px] font-medium uppercase tracking-[0.08em] text-subtle">{t('usage.license')}</div>
          <div className="flex flex-wrap gap-2">
            {p.product_pack && (
              <Chip label={t('usage.pack')} value={p.product_pack} />
            )}
            {p.pbp_cap && (
              <Chip label={t('usage.pbp')} value={p.pbp_cap} />
            )}
            {(() => {
              const e = entitlementsOf(p);
              const bits: string[] = [];
              if (e.wire_plan && e.wire_plan !== 'off') bits.push(`Wire ${e.wire_plan}`);
              if (e.onair) bits.push('On-Air');
              if (e.line_plan && e.line_plan !== 'off') bits.push(`Line ${e.line_plan}`);
              if (e.office) bits.push('Office');
              if (e.hall) bits.push('Hall');
              if (e.clips) bits.push('Clips');
              return bits.map((b) => <Chip key={b} label="" value={b} />);
            })()}
            <Chip label={t('usage.sports')} value={sports.length ? sports.join(', ') : t('usage.allSports')} />
            <Chip
              label={t('usage.competitions')}
              value={comps.length
                ? comps.join(', ')
                : (p.allowed_competition_ids ?? []).length
                  ? String(p.allowed_competition_ids.length)
                  : t('usage.allComps')}
            />
            <Chip label={t('usage.delay')} value={`${p.delivery_delay_ms} ms`} />
            {(p.allowed_teams ?? []).length > 0 && (
              <Chip label={t('usage.club')} value={p.allowed_teams!.map((team) => team.name).join(', ')} />
            )}
            {p.parent_company_name && (
              <Chip label={t('usage.league')} value={p.parent_company_name} />
            )}
          </div>
        </div>
      )}

      <form
        className="grid gap-4 xl:grid-cols-2"
        onSubmit={(e) => { e.preventDefault(); setMsg(null); save.mutate(); }}
      >
        <div className="card space-y-4 p-4">
          <div>
            <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-subtle">{t('usage.delivery')}</div>
            <p className="mt-1 text-[13px] text-slate-500">{t('usage.deliveryHint')}</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('usage.timezone')}>
              <select className="select" value={tz} onChange={(e) => setTz(e.target.value)}>
                {zones.map((z) => <option key={z} value={z}>{z}</option>)}
              </select>
            </Field>
            <Field label={t('usage.morningHour')}>
              <select className="select" value={mpHour} onChange={(e) => setMpHour(Number(e.target.value))}>
                {Array.from({ length: 24 }, (_, h) => (
                  <option key={h} value={h}>{`${String(h).padStart(2, '0')}:00`}</option>
                ))}
              </select>
            </Field>
            <Field label={t('usage.quietFrom')}>
              <input className="input" type="time" value={qhStart} onChange={(e) => setQhStart(e.target.value)} />
            </Field>
            <Field label={t('usage.quietUntil')}>
              <input className="input" type="time" value={qhEnd} onChange={(e) => setQhEnd(e.target.value)} />
            </Field>
          </div>
          <Toggle on={qhOn} onChange={setQhOn} label={t('usage.holdReels')} />
          <Toggle on={mpOn} onChange={setMpOn} label={t('usage.batchMorning')} />
          {hasOffice(p) && (
            <Toggle on={autoPack} onChange={setAutoPack} label="Automatski kolo pack posle poslednjeg FT" />
          )}
          <Toggle on={autoDigest} onChange={setAutoDigest} label="Club digest 5 min posle FT" />
          <Field label="Javni slug (match center /l/…)">
            <input className="input" value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} placeholder="arkus" />
          </Field>
          {hasHall(p) && (
            <Field label="Mesečni izveštaj sponzoru">
              <input className="input" type="email" value={sponsorEmail} onChange={(e) => setSponsorEmail(e.target.value)} placeholder="sponzor@arena.rs" />
            </Field>
          )}
          {slug && (
            <p className="text-[12px] text-slate-500">
              Javno: <a className="text-white underline" href={`http://127.0.0.1:8081/l/${slug}`} target="_blank" rel="noreferrer">/l/{slug}</a>
              {' · '}
              <a className="text-white underline" href={`http://127.0.0.1:8081/wire/${slug}`} target="_blank" rel="noreferrer">/wire/{slug}</a>
            </p>
          )}
        </div>

        <div className="card space-y-4 p-4">
          <div>
            <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-subtle">{t('usage.brand')}</div>
            <p className="mt-1 text-[13px] text-slate-500">{t('usage.brandHint')}</p>
          </div>
          <LogoUpload
            value={sponsorLogo}
            onChange={(url) => { setSponsorLogo(url); setLogoErr(null); }}
            onError={(code) => setLogoErr(t(code))}
            t={t}
          />
          {logoErr && <div className="alert-bad">{logoErr}</div>}
          <Field label={t('usage.sponsorLabel')}>
            <input
              className="input"
              value={sponsorLabel}
              onChange={(e) => setSponsorLabel(e.target.value)}
              placeholder={t('usage.sponsorLabelPh')}
            />
          </Field>
          {hasWhiteLabel(p) && (
            <Toggle on={poweredBy} onChange={setPoweredBy} label={t('usage.poweredBy')} />
          )}
          <div>
            <div className="mb-2 text-[11px] font-medium uppercase tracking-[0.08em] text-subtle">{t('usage.preview')}</div>
            <div className="rounded-lg bg-[#0b0d12] px-3 py-3 ring-1 ring-white/10">
              <div className="mb-2 text-[13px] font-medium text-white">ARKUS · live-score</div>
              <div className="flex items-center justify-between gap-3 border-t border-white/10 pt-2 text-[10px] text-slate-500">
                {sponsorLogo ? (
                  <span className="inline-flex min-w-0 items-center gap-2">
                    <img src={sponsorLogo} alt="" className="max-h-5 max-w-[88px] object-contain" />
                    {sponsorLabel && <span className="truncate">{sponsorLabel}</span>}
                  </span>
                ) : <span />}
                {poweredBy && <span className="shrink-0">Powered by Origo</span>}
              </div>
            </div>
          </div>
        </div>

        <div className="xl:col-span-2 flex flex-wrap items-center gap-3">
          <button type="submit" className="btn-primary" disabled={save.isPending}>
            {save.isPending ? t('usage.saving') : t('usage.save')}
          </button>
          {msg && <div className="text-sm text-slate-300">{msg}</div>}
        </div>
      </form>

      {u?.near_limit && (
        <div className="card p-3 text-sm text-warn">{t('usage.nearLimit')}</div>
      )}
    </div>
  );
}

function Stat({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className="card px-4 py-3">
      <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-subtle">{label}</div>
      <div className={`mt-1.5 text-[1.35rem] font-semibold tabular-nums leading-none ${warn ? 'text-warn' : 'text-white'}`}>{value}</div>
    </div>
  );
}

function Chip({ label, value }: { label: string; value: string }) {
  return (
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-white/[0.05] px-2.5 py-1 text-[12px] ring-1 ring-white/[0.06]">
      <span className="text-slate-500">{label}</span>
      <span className="truncate font-medium text-slate-100">{value}</span>
    </span>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="label">{label}</span>
      {children}
    </label>
  );
}

function Toggle({ on, onChange, label }: { on: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={() => onChange(!on)}
      className="flex w-full items-center justify-between gap-3 rounded-lg bg-white/[0.03] px-3 py-2.5 text-left text-sm text-slate-200 ring-1 ring-white/[0.06]"
    >
      <span>{label}</span>
      <span className={`relative h-5 w-9 shrink-0 rounded-full transition ${on ? 'bg-accent' : 'bg-white/15'}`}>
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition ${on ? 'left-4' : 'left-0.5'}`} />
      </span>
    </button>
  );
}

function Breakdown({ title, rows, empty }: { title: string; rows: [string, number][]; empty: string }) {
  return (
    <div className="card p-4">
      <div className="mb-2 text-[11px] font-medium uppercase tracking-[0.08em] text-subtle">{title}</div>
      {rows.map(([name, count]) => (
        <div key={name} className="flex justify-between gap-3 py-0.5 text-sm">
          <span className="truncate text-slate-300">{name}</span>
          <span className="tabular-nums text-white">{count}</span>
        </div>
      ))}
      {rows.length === 0 && <div className="text-sm text-slate-500">{empty}</div>}
    </div>
  );
}

function LogoUpload({
  value, onChange, onError, t,
}: {
  value: string;
  onChange: (url: string) => void;
  onError: (key: 'usage.logoBadType' | 'usage.logoTooBig') => void;
  t: (k: string) => string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);

  const take = async (file?: File | null) => {
    if (!file) return;
    setBusy(true);
    try {
      onChange(await fileToSponsorDataURL(file));
    } catch (e) {
      onError(e instanceof Error && e.message === 'too_large' ? 'usage.logoTooBig' : 'usage.logoBadType');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="label">{t('usage.logo')}</div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
        className="hidden"
        onChange={(e) => { void take(e.target.files?.[0]); e.target.value = ''; }}
      />
      {value ? (
        <div className="flex items-center gap-3 rounded-xl bg-white/[0.03] p-3 ring-1 ring-white/10">
          <div className="flex h-16 w-28 shrink-0 items-center justify-center rounded-lg bg-[#0b0d12] ring-1 ring-white/10">
            <img src={value} alt="" className="max-h-12 max-w-[6.5rem] object-contain" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm text-slate-200">{t('usage.logoHint')}</div>
            <div className="mt-2 flex flex-wrap gap-2">
              <button type="button" className="btn-secondary text-xs" onClick={() => inputRef.current?.click()}>
                {t('usage.logoReplace')}
              </button>
              <button type="button" className="btn-ghost text-xs" onClick={() => onChange('')}>
                {t('usage.logoRemove')}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <button
          type="button"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          onDragEnter={(e) => { e.preventDefault(); setDrag(true); }}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); void take(e.dataTransfer.files?.[0]); }}
          className={`flex w-full flex-col items-center justify-center rounded-xl border border-dashed px-4 py-8 text-center transition ${
            drag ? 'border-accent bg-accent/10' : 'border-white/15 bg-white/[0.02] hover:border-white/25'
          }`}
        >
          <span className="text-sm font-medium text-white">{t('usage.logoDrop')}</span>
          <span className="mt-1 text-[12px] text-slate-500">{t('usage.logoHint')}</span>
        </button>
      )}
    </div>
  );
}

const MAX_BYTES = 4 * 1024 * 1024;
const MAX_DATA = 220_000;

async function fileToSponsorDataURL(file: File): Promise<string> {
  if (file.size > MAX_BYTES) throw new Error('too_large');
  const svg = file.type === 'image/svg+xml' || /\.svg$/i.test(file.name);
  const raster = /image\/(png|jpeg|jpg|webp|gif)/i.test(file.type) || /\.(png|jpe?g|webp|gif)$/i.test(file.name);
  if (!svg && !raster) throw new Error('bad_type');
  if (svg) {
    const text = await file.text();
    if (text.length > 80_000) throw new Error('too_large');
    return `data:image/svg+xml;utf8,${encodeURIComponent(text)}`;
  }
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, 640 / bitmap.width, 240 / bitmap.height);
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('bad_type');
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close();
  let url = canvas.toDataURL('image/png');
  if (url.length > MAX_DATA) url = canvas.toDataURL('image/jpeg', 0.82);
  if (url.length > MAX_DATA) throw new Error('too_large');
  return url;
}
